import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { lcEnrollmentApi, lcTimeslotApi } from '@/api/lc'
import { tenantApi } from '@/api/tenant'
import { useChangeCourse, useCurrentEntitlement } from '@/hooks/admin/useContentFee'
import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@quebon/ui/components/dialog'
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from '@quebon/ui/components/field'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@quebon/ui/components/select'
import { Spinner } from '@quebon/ui/components/spinner'
import { Textarea } from '@quebon/ui/components/textarea'

const KST_DATE_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

function todayInKst(): string {
  const parts = Object.fromEntries(
    KST_DATE_FORMATTER.formatToParts(new Date()).map(({ type, value }) => [type, value]),
  )
  return `${parts.year}-${parts.month}-${parts.day}`
}

export interface CourseChangeLearner {
  userId: number
  name: string
  lcEnrollmentId: number
  /** 미전달이면 모달이 enrollment 상세를 조회해 현재 license를 알아낸다(roster 진입점). */
  licenseId?: number
  productName: string | null
  timeslotId: number | null
}

interface Props {
  learner: CourseChangeLearner
  onClose: () => void
  /** 성공 화면의 결제 CTA. 부모가 이 모달을 닫고 ContentFeePurchaseModal을 연다. 자동 open은 하지 않는다. */
  onRequestPayment: (userId: number) => void
}

/**
 * 과정 변경(A→B) — spec: content-fee-refund-course-change-spec.md §LMS UX.
 * 결제는 별도 단계다. 성공 후 결제 필요 상태와 명시적 CTA만 노출한다(축3 A, 엣지 7).
 */
export function CourseChangeModal({ learner, onClose, onRequestPayment }: Props) {
  const { t } = useTranslation(['admin', 'common'])
  const [targetLicenseId, setTargetLicenseId] = useState<number | undefined>()
  const [targetTimeslotId, setTargetTimeslotId] = useState<number | undefined>(learner.timeslotId ?? undefined)
  const [reason, setReason] = useState('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [changedProductName, setChangedProductName] = useState<string | null>(null)

  const licensesQ = useQuery({
    queryKey: ['activeLicenses'],
    queryFn: async () => (await tenantApi.getAllActiveLicenses()).data.data ?? [],
  })
  const timeslotsQ = useQuery({
    queryKey: ['lcTimeslots'],
    queryFn: () => lcTimeslotApi.getAll(),
  })
  // active 수강권(PURCHASE/ADMIN_TEST)이 남아 있으면 과정 변경 전에 환불/회수가 먼저다(INV-2).
  const entitlementQ = useCurrentEntitlement(learner.userId)
  const hasActiveEntitlement = entitlementQ.data != null

  const enrollmentQ = useQuery({
    queryKey: ['lcEnrollments', 'detail', learner.lcEnrollmentId],
    queryFn: () => lcEnrollmentApi.getById(learner.lcEnrollmentId),
    enabled: learner.licenseId == null,
  })
  const currentLicenseId = learner.licenseId ?? enrollmentQ.data?.data.data?.licenseId

  const mutation = useChangeCourse(learner.userId)

  // 서버 getAllActiveLicenses는 trial을 제외하지만 배정 허용·구독·유효기간까지 좁히지는 않는다.
  const today = todayInKst()
  const licenseItems = (licensesQ.data ?? [])
    .filter((license) =>
      license.id !== currentLicenseId &&
      license.allowAdminAssignment !== false &&
      (license.subscriptionStatus == null || license.subscriptionStatus === 'active') &&
      (license.validFrom == null || license.validFrom <= today) &&
      (license.validUntil == null || license.validUntil >= today),
    )
    .map((license) => ({
      value: String(license.id),
      label: license.productName ?? license.curriculumTitle ?? t('admin:roster.action.changeCourse.unnamedCourse'),
    }))
  const timeslotItems = (timeslotsQ.data?.data.data ?? []).map((timeslot) => ({
    value: String(timeslot.timeslotId),
    label: timeslot.maxStudents == null
      ? timeslot.name
      : `${timeslot.name} (${t('admin:roster.action.changeCourse.capacity', {
          current: timeslot.currentStudents,
          max: timeslot.maxStudents,
        })})`,
  }))

  const handleSubmit = () => {
    if (targetLicenseId == null) {
      setValidationError(t('admin:roster.action.changeCourse.licenseRequired'))
      return
    }
    const trimmed = reason.trim()
    if (!trimmed || trimmed.length > 1000) {
      setValidationError(t('admin:roster.action.changeCourse.reasonRequired'))
      return
    }
    setValidationError(null)
    mutation.mutate(
      {
        sourceEnrollmentId: learner.lcEnrollmentId,
        body: {
          targetLicenseId,
          targetTimeslotId: targetTimeslotId ?? null,
          reason: trimmed,
        },
      },
      {
        onSuccess: (response) => {
          setChangedProductName(response.data.data?.currentEnrollment.productName ?? null)
        },
      },
    )
  }

  const changed = changedProductName !== null || mutation.isSuccess

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('admin:roster.action.changeCourse.title', { name: learner.name })}</DialogTitle>
          <DialogDescription>
            {t('admin:roster.action.changeCourse.currentCourse', {
              course: learner.productName ?? '-',
            })}
          </DialogDescription>
        </DialogHeader>

        {changed ? (
          <div className="space-y-4">
            <Alert>
              <AlertTitle>{t('admin:roster.action.changeCourse.successTitle')}</AlertTitle>
              <AlertDescription>
                {t('admin:roster.action.changeCourse.paymentNeeded', {
                  course: changedProductName ?? '-',
                })}
              </AlertDescription>
            </Alert>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t('common:button.close')}
              </Button>
              <Button type="button" onClick={() => onRequestPayment(learner.userId)}>
                {t('admin:roster.action.changeCourse.paymentCta')}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <>
            {hasActiveEntitlement ? (
              <Alert>
                <AlertTitle>{t('admin:roster.action.changeCourse.refundFirstTitle')}</AlertTitle>
                <AlertDescription>{t('admin:roster.action.changeCourse.refundFirst')}</AlertDescription>
              </Alert>
            ) : (
              <Alert>
                <AlertDescription>
                  {t('admin:roster.action.changeCourse.warn')}{' '}
                  {t('admin:roster.action.changeCourse.textbookWarn')}
                </AlertDescription>
              </Alert>
            )}

            <FieldGroup>
              <Field data-invalid={validationError != null && targetLicenseId == null}>
                <FieldLabel>{t('admin:roster.action.changeCourse.licenseLabel')}</FieldLabel>
                <Select
                  items={licenseItems}
                  value={targetLicenseId != null ? String(targetLicenseId) : null}
                  onValueChange={(value) => {
                    setTargetLicenseId(value ? Number(value) : undefined)
                    setValidationError(null)
                  }}
                  disabled={hasActiveEntitlement || licensesQ.isLoading}
                >
                  <SelectTrigger className="w-full" aria-invalid={validationError != null && targetLicenseId == null}>
                    <SelectValue placeholder={t('admin:roster.action.changeCourse.licensePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {licenseItems.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </Field>

              <Field>
                <FieldLabel>{t('admin:roster.action.changeCourse.timeslotLabel')}</FieldLabel>
                <Select
                  items={timeslotItems}
                  value={targetTimeslotId != null ? String(targetTimeslotId) : null}
                  onValueChange={(value) => setTargetTimeslotId(value ? Number(value) : undefined)}
                  disabled={hasActiveEntitlement || timeslotsQ.isLoading}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={t('admin:roster.action.changeCourse.timeslotPlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {timeslotItems.map((item) => (
                        <SelectItem key={item.value} value={item.value}>
                          {item.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <FieldDescription>{t('admin:roster.action.changeCourse.timeslotHint')}</FieldDescription>
              </Field>

              <Field data-invalid={validationError != null && !reason.trim()}>
                <FieldLabel htmlFor="course-change-reason">
                  {t('admin:roster.action.changeCourse.reasonLabel')}
                </FieldLabel>
                <Textarea
                  id="course-change-reason"
                  value={reason}
                  maxLength={1000}
                  aria-invalid={validationError != null && !reason.trim()}
                  placeholder={t('admin:roster.action.changeCourse.reasonPlaceholder')}
                  onChange={(event) => {
                    setReason(event.target.value)
                    setValidationError(null)
                  }}
                  disabled={hasActiveEntitlement}
                />
                <FieldError>{validationError}</FieldError>
              </Field>
            </FieldGroup>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                {t('common:button.cancel')}
              </Button>
              <Button
                type="button"
                disabled={mutation.isPending || hasActiveEntitlement || entitlementQ.isLoading}
                onClick={handleSubmit}
              >
                {mutation.isPending ? <Spinner data-icon="inline-start" /> : null}
                {t('admin:roster.action.changeCourse.submit')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
